import { ArtistI } from '@tacans/shared-marketplace-layer';
import { useTranslation } from 'next-i18next';
import Link from 'next/link';
import React, { useMemo } from 'react';
import CopyToClipboard from 'react-copy-to-clipboard';
import { generatePath, RouteE } from 'src/constants/pages.constants';
import { NftBlockchainIcon } from 'src/features/common/collectible/components/nft-blockchain-icon';
import {
    StyledEllipsedText,
    StyledLink,
} from 'src/features/common/collectible/containers/nft-details-price-container/styles';
import { useCollectible } from 'src/features/common/collectible/hooks/collectible.hooks';
import { INftDetailsLocale } from 'src/features/common/collectible/types/locales';
import { useProfileLoginUrl } from 'src/features/common/profile/hooks/use-profile-login-url';
import { useDisplayName } from 'src/features/common/user/hooks/use-display-name.hooks';
import { useUserListEntities } from 'src/features/common/user/hooks/user-list.hooks';
import { useBlockChainsById } from 'src/features/common/wallet/hooks/wallet.hooks';
import { RowWrapper } from 'src/features/white-labels/ocean/collectible/containers/nft-details-price-container/row-wrapper';
import { FlexBox } from 'src/ui/common/atoms/flex-box';
import MiddleEllipsis from 'src/ui/common/molecules/middle-ellipsis';
import { CardBox } from 'src/ui/white-labels/ocean/layouts/card-box';
import { decodeHTMLEntities } from 'src/utils/html.utils';

export const NftDetailsPriceContainer = ({ artist }: { artist?: ArtistI }) => {
    const nft = useCollectible();
    const blockchains = useBlockChainsById();
    const { t } = useTranslation(['pages']);
    const users = useUserListEntities();
    const userName = useDisplayName(users[nft?.author!]);
    const userLink = useProfileLoginUrl(users[nft?.author!]);
    const artistLink = artist && generatePath(RouteE.CREATOR_PROFILE, { id: artist.id });

    const locale: INftDetailsLocale = useMemo(
        () => ({
            ...t('nft.view.details', { returnObjects: true }),
            roles: t('nft.view.userRoles', { returnObjects: true }),
        }),
        [t]
    );

    const blockchain = blockchains[nft?.blockchainId!];

    if (!nft) {
        return null;
    }

    return (
        <CardBox allowCollapse>
            <CardBox.Heading>{locale.title}</CardBox.Heading>
            <CardBox.Content>
                {nft.transactionHash && (
                    <RowWrapper label={locale.mintAddress}>
                        <FlexBox alignItems="center">
                            <CopyToClipboard text={nft.transactionHash}>
                                <StyledEllipsedText size="sm" uppercase>
                                    <MiddleEllipsis text={nft.transactionHash} />
                                </StyledEllipsedText>
                            </CopyToClipboard>
                            <NftBlockchainIcon blockchainId={nft.blockchainId} />
                        </FlexBox>
                    </RowWrapper>
                )}
                <RowWrapper label={locale.roles.author}>
                    <Link href={userLink} prefetch={false}>
                        <StyledLink>{decodeHTMLEntities(userName)}</StyledLink>
                    </Link>
                </RowWrapper>
                {artist && (
                    <RowWrapper label={locale.roles.creator}>
                        <Link href={artistLink!} prefetch={false}>
                            <StyledLink>{artist!.displayName}</StyledLink>
                        </Link>
                    </RowWrapper>
                )}
                <RowWrapper label={locale.blockchainType}>{blockchain?.name}</RowWrapper>
                <RowWrapper label={locale.creatorRoyalties}>{nft.royalties ?? 0}%</RowWrapper>
            </CardBox.Content>
        </CardBox>
    );
};
